import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { UserPlus, Search, X } from "lucide-react";
import api from "../lib/api";
import PatientModal from "./modals/PatientModal";

export default function PatientSelect({ value, onChange, disabled = false, "data-testid": tid = "patient-select" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (disabled || !query || query.length < 1) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients", { params: { q: query } });
        setResults(data.slice(0, 8));
        setOpenList(true);
      } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query, disabled]);

  if (value) {
    return (
      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-3 py-2" data-testid={`${tid}-selected`}>
        <span className="font-medium text-blue-900">{value.first_name} {value.last_name}
          {value.phone ? <span className="text-xs text-blue-500 ml-2">{value.phone}</span> : null}
        </span>
        {!disabled && <button data-testid={`${tid}-clear`} onClick={() => onChange?.(null)} className="text-blue-400 hover:text-blue-600"><X size={16} /></button>}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid={`${tid}-input`}
          placeholder="Cerca paziente per nome o telefono..."
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpenList(true)}
          className="pl-9 rounded-xl"
        />
      </div>
      {!disabled && openList && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto" data-testid={`${tid}-results`}>
          {results.map((p) => (
            <button
              key={p.id}
              data-testid={`${tid}-option-${p.id}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex justify-between items-center"
              onClick={() => { onChange?.(p); setQuery(""); setOpenList(false); }}
            >
              <span className="font-medium">{p.first_name} {p.last_name}</span>
              <span className="text-xs text-slate-400">{p.phone}</span>
            </button>
          ))}
          <button
            data-testid={`${tid}-new`}
            className="w-full text-left px-3 py-2 text-blue-600 hover:bg-blue-50 flex items-center gap-2 border-t"
            onClick={() => { setShowNew(true); setOpenList(false); }}
          >
            <UserPlus size={16} /> Nuovo paziente
          </button>
        </div>
      )}
      {!disabled && !openList && (
        <Button variant="ghost" size="sm" data-testid={`${tid}-new-btn`} className="mt-1 text-blue-600" onClick={() => setShowNew(true)}>
          <UserPlus size={14} className="mr-1" /> Nuovo paziente
        </Button>
      )}
      {!disabled && showNew && (
        <PatientModal
          open={showNew}
          onClose={() => setShowNew(false)}
          onSaved={(p) => { onChange?.(p); setShowNew(false); }}
        />
      )}
    </div>
  );
}
